import { useCallback, useState } from 'react';
import { treasuryApi } from '../services/api/endpoints/treasury';
import { useBusinessUnitStore } from '../stores/businessUnitStore';
import type { TreasuryAccount, TreasuryAccountType } from '../types/treasury';
import type { CurrencyCode } from '../config/currency';

/** Payload de création d'un compte de trésorerie (front -> backend) */
export interface CreateTreasuryAccountPayload {
  name: string;
  type: TreasuryAccountType;
  accountNumber: string;
  bankName?: string;
  currency: CurrencyCode;
  isActive: boolean;
  businessUnitId?: string;
  businessUnitType?: string;
}

/**
 * Hook de gestion des comptes de trésorerie.
 *
 * Couvre les 5 types : BANK, MICROFINANCE, COOPERATIVE, VSLA, MOBILE_MONEY.
 * S'appuie sur l'API existante `treasuryApi`.
 */
export function useTreasury() {
  const [accounts, setAccounts] = useState<TreasuryAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { effectiveFilter } = useBusinessUnitStore();

  const loadAccounts = useCallback(
    async (params?: { type?: TreasuryAccountType; isActive?: boolean }) => {
      try {
        setLoading(true);
        setError(null);
        // Filtre multi-tenant : le backend acceptait déjà businessUnitId
        // (TreasuryController:48-55) mais la page ne le passait jamais →
        // pour une coopérative, TOUS les comptes de TOUS les coopérants
        // apparaissaient mélangés. Maintenant on respecte la sélection
        // sidebar (siège / branche / point de vente).
        const response = await treasuryApi.getAccounts({
          ...params,
          businessUnitId: effectiveFilter.businessUnitId,
          businessUnitType: effectiveFilter.businessUnitType,
        } as any);
        const payload =
          (response as any)?.data?.data ??
          (response as any)?.data ??
          response;
        const list: TreasuryAccount[] = Array.isArray(payload)
          ? payload
          : payload?.data ?? [];
        setAccounts(list);
      } catch (err: any) {
        setError(err?.message || 'Erreur lors du chargement des comptes');
        setAccounts([]);
      } finally {
        setLoading(false);
      }
    },
    [effectiveFilter.businessUnitId, effectiveFilter.businessUnitType],
  );

  const createAccount = useCallback(
    async (data: CreateTreasuryAccountPayload): Promise<TreasuryAccount | null> => {
      try {
        setSaving(true);
        setError(null);
        // Si l'utilisateur a une unité commerciale sélectionnée dans la
        // sidebar et que le payload n'en spécifie pas, on hérite — sinon
        // le compte est créé "siège" et n'apparaîtra plus quand l'utilisateur
        // filtre sur l'unité où il pensait l'avoir créé.
        const payloadWithUnit = {
          ...data,
          businessUnitId: data.businessUnitId ?? effectiveFilter.businessUnitId,
          businessUnitType: data.businessUnitType ?? effectiveFilter.businessUnitType,
        };
        const response = await treasuryApi.createAccount(payloadWithUnit as any);
        const payload =
          (response as any)?.data?.data ??
          (response as any)?.data ??
          response;
        const created: TreasuryAccount = (payload?.data ?? payload) as TreasuryAccount;
        if (created?.id) {
          setAccounts((prev) => [created, ...prev]);
        }
        return created ?? null;
      } catch (err: any) {
        setError(err?.message || 'Erreur lors de la création du compte');
        return null;
      } finally {
        setSaving(false);
      }
    },
    [effectiveFilter.businessUnitId, effectiveFilter.businessUnitType],
  );

  const deleteAccount = useCallback(async (id: string): Promise<boolean> => {
    try {
      setSaving(true);
      setError(null);
      await treasuryApi.deleteAccount(id);
      setAccounts((prev) => prev.filter((a) => a.id !== id));
      return true;
    } catch (err: any) {
      setError(err?.message || 'Erreur lors de la suppression');
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  return {
    accounts,
    loading,
    saving,
    error,
    loadAccounts,
    createAccount,
    deleteAccount,
  };
}
